import { useState } from "react";

type Category = "Product" | "Security" | "Fees" | "Developer" | "Roadmap";

interface FaqItem {
  q: string;
  a: string;
  cat: Category;
}

const FAQS: FaqItem[] = [
  {
    q: "What is Axon in one sentence?",
    a: "A non-custodial execution harness connecting AI agents to crypto markets — Hyperliquid perps, Uniswap V3 spot, Aave V3 lending, GMX v2. Your key stays encrypted on your machine; every intent is validated against your policy before it fires.",
    cat: "Product",
  },
  {
    q: "Who holds my keys and funds?",
    a: "You do. Owner EOA generated locally, encrypted with scrypt + AES-256-GCM. Operator never sees it, never stores it, never touches your funds.",
    cat: "Security",
  },
  {
    q: "What stops a rogue agent from draining my wallet?",
    a: "Three layers: trade-only venue credentials (no withdraw), local RiskGuard pre-signature validation (leverage / loss / venue / kill switch), and deterministic rejection codes the agent can reason about — not silent drops.",
    cat: "Security",
  },
  {
    q: "How does Axon make money?",
    a: "Cut of HL Builder Code fees, not user fees. Per-trade USDC share settled same-block on Hyperliquid. No deposit, withdrawal, or subscription fees.",
    cat: "Fees",
  },
  {
    q: "Why did Axon start with Hyperliquid?",
    a: "Deepest on-chain perps book and the only venue with a same-block on-chain Builder Code program. Axon also ships Uniswap V3 + PancakeSwap V3 (spot; swap execute behind per-venue env flags), Aave V3 (supply/withdraw/repay; borrow gated by policy.allowBorrow), GMX v2 (onchain perps, read-side), and Phoenix on Solana (read-side). Execute on the roadmap for the read-side venues.",
    cat: "Product",
  },
  {
    q: "Does Axon support spot trading?",
    a: "Yes — Uniswap V3 on Arbitrum, Base, and Polygon, plus PancakeSwap V3 on BNB Chain. Reads default-on; swap submission gated behind per-venue env flags (AXON_UNISWAP_EXECUTE=1 / AXON_PANCAKESWAP_EXECUTE=1) until first user dogfood. Phoenix on Solana is read-side today.",
    cat: "Product",
  },
  {
    q: "Can my AI agent trade autonomously?",
    a: "Yes. After `axon skills install`, your agent in Claude Code, Cursor, Codex, Hermes, etc. has 30+ typed MCP tools to open/close perps, swap on Uniswap, manage Aave health factors, run signed strategies, and act on the agent-alert surface. RiskGuard validates every intent against your policy *before* it signs — leverage caps, daily loss caps, slippage limits, HF floors, kill switch.",
    cat: "Product",
  },
  {
    q: "What can people actually do with this besides trade?",
    a: "Any onchain action your policy allows: swap tokens on Uniswap V3, supply / withdraw / repay on Aave V3, rebalance a portfolio, react to alerts (kill-switch, SL/TP, HF), buy and run x402-paid strategies from the marketplace, or schedule a strategy to run on a cron.",
    cat: "Product",
  },
  {
    q: "What chains are supported?",
    a: "Six EVM mainnets: Arbitrum, Base, Polygon, Optimism, Avalanche, BNB Smart Chain — plus an Arbitrum Sepolia testnet. Solana is read-side: balances plus Phoenix markets and tickers, with on-chain execution on the roadmap.",
    cat: "Product",
  },
  {
    q: "What happens while I'm away from my computer?",
    a: "RiskGuard runs autonomously: SL/TP sweep every 30s, health-factor sweep every 60s. Typed alerts emit when something fires. Your agent calls get_pending_alerts first thing on its next turn.",
    cat: "Security",
  },
  {
    q: "How does my agent connect to Axon?",
    a: "Run `axon skills install` once — auto-wires 30+ typed MCP tools and a skill into Claude Code, Claude Desktop, Cursor, Codex, Continue, Windsurf, Cline, and Hermes (8 runtimes). Or import @axon-trading/sdk for a custom client.",
    cat: "Developer",
  },
  {
    q: "What's the on-chain footprint?",
    a: "Two Arbitrum contracts: ReferrerRegistry (attribution) and AuditAnchor (weekly Merkle root). Venues use their canonical deployments. Axon adds the policy + agent surface.",
    cat: "Security",
  },
  {
    q: "Can I verify my trades were logged correctly?",
    a: "Yes. Every intent + signature + fee writes to an append-only SQLite log, SHA-256 hash-chained. Weekly Merkle root anchored on-chain via AuditAnchor.",
    cat: "Security",
  },
  {
    q: "Who owns Axon? Is this a hosted service?",
    a: "Strykr Labs. CLI / SDK / MCP server ship as npm packages — run locally. No hosted backend in the trade path. Keys, audit log, policy all stay on your device.",
    cat: "Product",
  },
  {
    q: "What's on the roadmap?",
    a: "The desktop app is already out in early access. GMX v2 execute and a hosted Relay bridge flow are the near-term focus. Synthetix Perps v3, more chains, and Solana are exploratory. The canonical list lives in ROADMAP.md.",
    cat: "Roadmap",
  },
];

const CATEGORIES: Array<"All" | Category> = ["All", "Product", "Security", "Fees", "Developer", "Roadmap"];

export function FAQ() {
  const [activeCat, setActiveCat] = useState<"All" | Category>("All");
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const visible = FAQS
    .map((f, i) => ({ ...f, i }))
    .filter((f) => activeCat === "All" || f.cat === activeCat);

  const toggle = (i: number) => {
    setOpenIdx((prev) => (prev === i ? null : i));
  };

  return (
    <section id="faq" className="ax-section">
      {/* FAQPage structured data — generated from the same FAQS array so
          it can never drift from what's rendered. Google reads inline
          JSON-LD anywhere in the document and may surface these as FAQ
          rich results / People-Also-Ask answers. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      <div className="ax-section-head">
        <div className="ax-eyebrow">04 · FAQ</div>
        <h2 className="ax-h2">
          Common questions.<br />
          <span style={{ color: "var(--at-fg-dim)" }}>Direct answers.</span>
        </h2>
      </div>

      <div className="ax-faq-cats" role="tablist" aria-label="Filter by category">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            role="tab"
            aria-selected={activeCat === c}
            onClick={() => {
              setActiveCat(c);
              setOpenIdx(null);
            }}
            className={"ax-faq-cat" + (activeCat === c ? " active" : "")}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="ax-faq-list">
        {visible.map((f) => {
          const isOpen = openIdx === f.i;
          return (
            <div key={f.i} className={"ax-faq-item" + (isOpen ? " open" : "")}>
              <button
                className="ax-faq-q"
                onClick={() => toggle(f.i)}
                aria-expanded={isOpen}
                aria-controls={"ax-faq-a-" + f.i}
              >
                <span className="ax-faq-q-cat">{f.cat}</span>
                <span className="ax-faq-q-text">{f.q}</span>
                <span className="ax-faq-q-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              </button>
              <div
                id={"ax-faq-a-" + f.i}
                className="ax-faq-a-wrap"
                role="region"
                aria-hidden={!isOpen}
              >
                <div className="ax-faq-a">{f.a}</div>
              </div>
            </div>
          );
        })}

        {visible.length === 0 && (
          <div className="ax-faq-empty">No questions in this category yet.</div>
        )}
      </div>

      <style>{`
        .ax-faq-cats {
          display: flex; flex-wrap: wrap; gap: 6px;
          margin-bottom: 24px;
        }
        .ax-faq-cat {
          font-family: var(--at-font-mono);
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 7px 12px;
          background: transparent;
          border: 1px solid var(--at-hairline);
          border-radius: var(--at-radius-full);
          color: var(--at-fg-dim);
          cursor: pointer;
          transition: border-color var(--at-dur-fast) var(--at-ease),
                      color var(--at-dur-fast) var(--at-ease),
                      background var(--at-dur-fast) var(--at-ease);
        }
        .ax-faq-cat:hover { border-color: var(--at-hairline-strong); color: var(--at-fg); }
        .ax-faq-cat.active {
          background: rgba(74, 222, 128, 0.08);
          border-color: rgba(74, 222, 128, 0.4);
          color: var(--at-accent);
        }

        .ax-faq-list {
          display: flex; flex-direction: column;
          border-top: 1px solid var(--at-hairline);
        }
        .ax-faq-item {
          border-bottom: 1px solid var(--at-hairline);
          transition: background var(--at-dur-fast) var(--at-ease);
        }
        .ax-faq-item:hover { background: rgba(255,255,255,0.015); }
        .ax-faq-item.open { background: rgba(255,255,255,0.02); }

        .ax-faq-q {
          display: grid;
          grid-template-columns: 90px 1fr 24px;
          align-items: center;
          gap: 18px;
          width: 100%;
          padding: 22px 4px;
          background: transparent;
          border: 0;
          color: var(--at-fg);
          text-align: left;
          cursor: pointer;
          font: inherit;
        }
        .ax-faq-q-cat {
          font-family: var(--at-font-mono);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--at-muted);
        }
        .ax-faq-item.open .ax-faq-q-cat { color: var(--at-accent); }
        .ax-faq-q-text {
          font-size: 17px;
          font-weight: 500;
          letter-spacing: -0.005em;
          line-height: 1.4;
        }
        .ax-faq-q-icon {
          display: inline-flex; justify-content: center; align-items: center;
          color: var(--at-fg-dim);
          transition: transform var(--at-dur-med) var(--at-ease),
                      color var(--at-dur-fast) var(--at-ease);
        }
        .ax-faq-item.open .ax-faq-q-icon {
          transform: rotate(180deg);
          color: var(--at-accent);
        }

        .ax-faq-a-wrap {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows var(--at-dur-med) var(--at-ease);
        }
        .ax-faq-item.open .ax-faq-a-wrap {
          grid-template-rows: 1fr;
        }
        .ax-faq-a-wrap > .ax-faq-a {
          overflow: hidden;
        }
        .ax-faq-a {
          padding: 0 4px 24px 112px;
          font-size: 15px;
          line-height: 1.65;
          color: var(--at-fg-dim);
          max-width: 760px;
        }

        .ax-faq-empty {
          padding: 32px 4px;
          font-family: var(--at-font-mono);
          font-size: 13px;
          color: var(--at-muted);
          text-align: center;
        }

        @media (max-width: 720px) {
          .ax-faq-q {
            grid-template-columns: 1fr 24px;
            gap: 12px;
            padding: 18px 4px;
          }
          .ax-faq-q-cat {
            grid-column: 1 / -1;
            font-size: 9px;
            letter-spacing: 0.2em;
            order: -1;
            margin-bottom: -6px;
          }
          .ax-faq-q-text { font-size: 15px; }
          .ax-faq-a { padding: 0 4px 18px 4px; font-size: 14px; }
        }
      `}</style>
    </section>
  );
}
